import { useEffect, useRef, useState, type FormEvent } from "react";
import {
  ArrowDown,
  ArrowUp,
  ArrowsCounterClockwise,
  Plus,
  PencilSimple,
  FileArrowUp,
  Trash,
} from "@phosphor-icons/react";
import { effectiveProductCategory, todayInShanghai, type CodeCategoryNode } from "@longjie/shared";
import { ApiError, request } from "./api";
import {
  Button,
  EmptyState,
  Field,
  LoadError,
  Modal,
  StatusBadge,
} from "./components";
import type {
  InventoryMovement,
  InventoryMovementType,
  InventorySkuOption,
  InventoryStockRow,
  CatalogResponse,
} from "./types";
import { formatDate } from "./ui-utils";
import type { CodeCategoryProduct } from "./CodeCategoryBrowser";
import { ProductCategoryFilter } from "./ProductCategoryFilter";
import { DealerCatalogLayout } from "./DealerCatalogLayout";
import { useSynchronizedCodeCategories } from "./code-category-sync";
import { CategoriesAdmin, CodeCategoryEditor, CategoryProductEditor } from "./CategoriesAdmin";
import "./categories.css";
import "./inventory-categories.css";
import "./inventory-manual.css";
import { InventorySheetImport } from "./InventorySheetImport";
import { ProductSearch } from "./ProductSearch";
import { useProductSearch } from "./useProductSearch";
import { productSearchPath } from "./catalog-selection";
import { ProductImage } from "./ProductImage";
import { MobileDisclosure } from "./MobileDisclosure";
import { MobileSkuCard } from "./MobileSkuCard";
import { MobileCategoryPicker } from "./MobileCategoryPicker";
import { usePhoneLayout } from "./usePhoneLayout";
import { useCatalogRefresh } from "./useCatalogRefresh";

type InventoryAdminProps = {
  isAdmin: boolean;
  canManageCatalog?: boolean;
  canManageCategories?: boolean;
  canManageProducts?: boolean;
  onPurchaseImport?: (() => void) | undefined;
  notify: (
    message: string,
    tone?: "success" | "error" | "info",
  ) => void;
};

type Pagination = {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
};

type StockResponse = {
  stats: {
    totalSkuCount: number;
    stockedSkuCount: number;
    totalQuantity: number;
  };
  stock: InventoryStockRow[];
  pagination: Pagination;
};

type MovementResponse = {
  movements: InventoryMovement[];
  pagination: Pagination;
};

const movementLabels: Record<
  InventoryMovementType,
  {
    label: string;
    tone: "success" | "info" | "warning";
    direction: "in" | "out";
  }
> = {
  PURCHASE_IN: { label: "采购入库", tone: "success", direction: "in" },
  SALES_OUT: { label: "销售出库", tone: "info", direction: "out" },
  ADJUSTMENT_IN: { label: "盘点入库", tone: "success", direction: "in" },
  ADJUSTMENT_OUT: { label: "盘点出库", tone: "warning", direction: "out" },
};

function loadErrorMessage(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

function totalMovementQuantity(movement: InventoryMovement): number {
  return movement.lines.reduce((sum, line) => sum + line.quantity, 0);
}

export function InventoryAdmin({ isAdmin, canManageCatalog = isAdmin, canManageCategories = canManageCatalog, canManageProducts = canManageCatalog, onPurchaseImport, notify }: InventoryAdminProps) {
  const isPhone = usePhoneLayout();
  const [view, setView] = useState<"stock" | "movements" | "categories">("stock");
  const [categoryNodes, setCodeCategories] = useState<CodeCategoryNode[]>([]);
  const codeCategories = useSynchronizedCodeCategories(categoryNodes);
  const [categoryLoading, setCategoryLoading] = useState(true);
  const [categoryError, setCategoryError] = useState("");
  const [categoryPrefix, setCategoryPrefix] = useState("");
  const [mobileAllProducts, setMobileAllProducts] = useState(false);
  const [categoryEditor, setCategoryEditor] = useState<CodeCategoryNode | null>(null);
  const [productEditor, setProductEditor] = useState<CodeCategoryProduct | null>(null);
  const [unclassifiedProductCount, setUnclassifiedProductCount] = useState(0);
  const [stock, setStock] = useState<StockResponse | null>(null);
  const [movements, setMovements] = useState<MovementResponse | null>(null);
  const [stockLoading, setStockLoading] = useState(true);
  const [movementLoading, setMovementLoading] = useState(false);
  const [stockError, setStockError] = useState("");
  const [movementError, setMovementError] = useState("");
  const [movementType, setMovementType] = useState<
    InventoryMovementType | ""
  >("");
  const [stockPage, setStockPage] = useState(1);
  const [movementPage, setMovementPage] = useState(1);
  const [stockReload, setStockReload] = useState(0);
  const [movementReload, setMovementReload] = useState(0);
  useCatalogRefresh(() => { setStockReload((value) => value + 1); });
  const stockSearch = useProductSearch({ onCommit: () => {
    setStockLoading(true); setStockError(""); setStockPage(1);
  } });
  const movementSearch = useProductSearch({ onCommit: () => {
    setMovementLoading(true); setMovementError(""); setMovementPage(1);
  } });
  const activeSearch = view === "stock" ? stockSearch : movementSearch;
  const [movementModalType, setMovementModalType] =
    useState<InventoryMovementType | null>(null);
  const [reverseTarget, setReverseTarget] =
    useState<InventoryMovement | null>(null);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    void request<{ codeCategories?: CodeCategoryNode[]; unclassifiedProductCount?: number }>("/api/v1/admin/categories?includeProducts=false", { signal: controller.signal })
      .then((result) => {
        if (!active) return;
        setCategoryError("");
        const nodes = result.codeCategories ?? [];
        setCodeCategories(nodes);
        setUnclassifiedProductCount(result.unclassifiedProductCount ?? 0);
        setCategoryPrefix((previous) => {
          if (!previous || nodes.some((node) => node.prefix === previous)) return previous;
          return nodes.filter((node) => previous.startsWith(node.prefix)).at(-1)?.prefix ?? "";
        });
      })
      .catch((error: unknown) => { if (active) setCategoryError(loadErrorMessage(error, "商品分类加载失败")); })
      .finally(() => { if (active) setCategoryLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [stockReload]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const path = productSearchPath("/api/v1/admin/inventory/stock", stockSearch.query, categoryPrefix ? `code:${categoryPrefix}` : "", { page: stockPage, pageSize: 30 });
    void request<StockResponse>(path, { signal: controller.signal })
      .then((result) => {
        if (!active) return;
        if (stockPage > result.pagination.totalPages) {
          setStockPage(Math.max(1, result.pagination.totalPages));
          return;
        }
        setStockError("");
        setStock(result);
      })
      .catch((error: unknown) => {
        if (active) {
          setStockError(loadErrorMessage(error, "库存余额加载失败"));
        }
      })
      .finally(() => {
        if (active) setStockLoading(false);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [stockPage, stockSearch.query, stockSearch.revision, stockReload, categoryPrefix]);

  useEffect(() => {
    if (view !== "movements") return;
    let active = true;
    const controller = new AbortController();
    const params = new URLSearchParams({
      query: movementSearch.query,
      page: String(movementPage),
      pageSize: "30",
    });
    if (movementType) params.set("movementType", movementType);
    void request<MovementResponse>(
      `/api/v1/admin/inventory/movements?${params}`,
      { signal: controller.signal },
    )
      .then((result) => {
        if (active) setMovements(result);
      })
      .catch((error: unknown) => {
        if (active) {
          setMovementError(loadErrorMessage(error, "库存流水加载失败"));
        }
      })
      .finally(() => {
        if (active) setMovementLoading(false);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [movementPage, movementSearch.query, movementSearch.revision, movementReload, movementType, view]);

  function applySearch(event: FormEvent) {
    event.preventDefault();
    activeSearch.submit();
  }

  function reloadAll() {
    setStockLoading(true);
    setStockError("");
    setStockPage(1);
    setMovementLoading(true);
    setMovementError("");
    setCategoryLoading(true);
    setCategoryError("");
    setStockReload((value) => value + 1);
    setMovementReload((value) => value + 1);
  }

  const stats = stock?.stats;
  const visibleCategoryPrefix = effectiveProductCategory(stockSearch.query, categoryPrefix);
  const selectedCategory = codeCategories.find((node) => node.prefix === visibleCategoryPrefix);
  const productCount = codeCategories.filter((node) => node.parentPrefix === null).reduce((sum, node) => sum + node.productCount, 0) + unclassifiedProductCount;
  const hideRootStock = isPhone && !mobileAllProducts && !stockSearch.query && !visibleCategoryPrefix && codeCategories.length > 0;
  function selectPhoneCategory(value: string, allProducts = false) {
    stockSearch.reset("");
    setMobileAllProducts(allProducts);
    selectCategory(codeCategories.find(node => `code:${node.prefix}` === value));
    window.scrollTo({ top: 0, behavior: "instant" });
  }
  function selectCategory(node?: CodeCategoryNode) {
    if (categoryPrefix === (node?.prefix ?? "")) return;
    setStockLoading(true);
    setStockError("");
    setCategoryPrefix(node?.prefix ?? "");
    setStockPage(1);
  }
  return (
    <>
      <section className="admin-intro inventory-intro">
        <div className="inventory-intro__copy">
          <p className="eyebrow">FINISHED GOODS INVENTORY</p>
          <h2>按商品分类查看和管理成品库存</h2>
          <p>选分类查看商品及实际库存。订单确认出货后自动扣库存并生成销售出库流水。</p>
        </div>
        <div className="inventory-view-switch" role="group" aria-label="库存查看方式">
          <button
            className={view === "stock" ? "active" : ""}
            aria-pressed={view === "stock"}
            onClick={() => {
              setView("stock");
              stockSearch.reset(stockSearch.query);
              movementSearch.reset(movementSearch.query);
            }}
          >
            库存余额
          </button>
          <button
            className={view === "movements" ? "active" : ""}
            aria-pressed={view === "movements"}
            onClick={() => {
              if (view === "movements") return;
              setMovementLoading(true);
              setMovementError("");
              setView("movements");
              stockSearch.reset(stockSearch.query);
              movementSearch.reset(movementSearch.query);
            }}
          >
            进出流水
          </button>
          <button className={view === "categories" ? "active" : ""} aria-pressed={view === "categories"}
            onClick={() => setView("categories")}>分类名称维护</button>
        </div>
        {isAdmin && (
          <div className="inventory-intro__actions">
            <Button onClick={() => setMovementModalType("PURCHASE_IN")}>
              <Plus weight="bold" />
              采购入库
            </Button>
            <Button variant="secondary" onClick={() => setMovementModalType("SALES_OUT")}>
              <ArrowUp weight="bold" />
              出库 / 盘点
            </Button>
          </div>
        )}
      </section>
      <section className="metric-rail inventory-metrics">
        <article className="metric metric--blue">
          <span>01</span>
          <small>商品总种数</small>
          <strong>{categoryLoading || categoryError ? "—" : productCount}</strong>
          <p>全库商品，不重复累计下级分类</p>
        </article>
        <article className="metric metric--steel">
          <span>02</span>
          <small>筛选结果规格数</small>
          <strong>{stockLoading || stockError ? "—" : stats?.totalSkuCount ?? "—"}</strong>
          <p>当前分类和搜索条件下的全部规格</p>
        </article>
        <article className="metric metric--orange">
          <span>03</span>
          <small>有库存规格数</small>
          <strong>{stockLoading || stockError ? "—" : stats?.stockedSkuCount ?? "—"}</strong>
          <p>筛选结果中库存大于零的规格</p>
        </article>
      </section>

      {isPhone && view === "stock" && <section className="staff-catalog staff-catalog-toolbar" aria-label="查找库存商品"><ProductSearch search={stockSearch} ariaLabel="搜索库存余额" busy={stockLoading} /></section>}
      {view === "stock" && !isPhone && <div className="inventory-classification-note">分类数字表示商品种数，上级包含下级；库存按每个规格的单位单独显示。</div>}
      {view === "categories" ? <CategoriesAdmin key={`${canManageCategories}:${canManageProducts}`} settingsOnly isAdmin={canManageCategories} canManageProducts={canManageProducts} notify={notify} onChanged={reloadAll} /> : <DealerCatalogLayout
        enabled={view === "stock"} className="inventory-category-workspace category-workspace staff-catalog" storageKey="longjie.admin-category-width"
        sidebarId="inventory-category-panel" contentId="inventory-product-panel" defaultWidth={420} minWidth={340} contentMinWidth={440}>
      {view === "stock" && <aside id="inventory-category-panel" className="inventory-category-sidebar category-navigation" aria-label="仓库商品分类">
        {categoryLoading ? <p role="status">正在加载分类…</p> : categoryError ? <LoadError title="商品分类暂时无法加载" description={categoryError} onRetry={reloadAll} />
          : isPhone ? <>
            <MobileCategoryPicker nodes={codeCategories} legacyCategories={[]} selectedValue={visibleCategoryPrefix ? `code:${visibleCategoryPrefix}` : "all"}
              compact={Boolean(stockSearch.query) || mobileAllProducts} showAllProducts={mobileAllProducts && !stockSearch.query}
              headingAction={selectedCategory && canManageCategories && <Button variant="secondary" aria-label="编辑当前分类" title="编辑当前分类" onClick={() => setCategoryEditor(selectedCategory)}><PencilSimple /></Button>}
              onShowAllProducts={() => selectPhoneCategory("all", true)} onSelect={selectPhoneCategory} />
          </> : <MobileDisclosure title="商品分类" resetKey={visibleCategoryPrefix} description={selectedCategory?.name ?? "全部商品"}>
            <ProductCategoryFilter nodes={codeCategories} value={visibleCategoryPrefix ? `code:${visibleCategoryPrefix}` : ""}
              onChange={(value) => selectCategory(codeCategories.find((node) => `code:${node.prefix}` === value))}
              isAdmin={canManageCategories} {...(canManageCategories ? { onEditCategory: setCategoryEditor } : {})} />
            {codeCategories.length === 0 && <p>暂无可识别的编码分类，请在商品目录维护商品编码。</p>}
          </MobileDisclosure>}
      </aside>}
      <div id="inventory-product-panel" className="inventory-category-content">
      {view === "stock" && !isPhone && <div className="inventory-selected-category"><h3>{selectedCategory ? `${selectedCategory.path} · ${selectedCategory.name ?? `第 ${selectedCategory.depth} 级分类`}` : "全部商品"}</h3>
        <p>{selectedCategory ? `共 ${selectedCategory.productCount} 种商品（含全部下级）` : `共 ${productCount} 种商品`} · 库存按规格显示</p></div>}
      {(!isPhone || view !== "stock") && <form className="admin-toolbar inventory-toolbar" onSubmit={applySearch}>
        <ProductSearch search={activeSearch} buttonLabel="查询"
          ariaLabel={view === "stock" ? "搜索库存余额" : "搜索库存进出流水"}
          {...(view === "movements" ? { placeholder: "搜索商品名称、编码、规格、单号或往来单位" } : {})} />
        {view === "movements" && (
          <select
            value={movementType}
            onChange={(event) => {
              setMovementLoading(true);
              setMovementError("");
              setMovementPage(1);
              setMovementType(
                event.target.value as InventoryMovementType | "",
              );
            }}
            aria-label="筛选库存单据类型"
          >
            <option value="">全部类型</option>
            {Object.entries(movementLabels).map(([value, meta]) => (
              <option key={value} value={value}>
                {meta.label}
              </option>
            ))}
          </select>
        )}
      </form>}
      {isPhone && view === "stock" && <div className="staff-catalog-summary"><h2>{stockSearch.query ? "搜索结果" : hideRootStock ? "按分类查找库存" : "库存规格"}</h2>
        <p>{hideRootStock ? "逐级打开分类，或输入编号查找" : `共 ${stock?.pagination.total ?? 0} 个规格${selectedCategory && codeCategories.some(node => node.parentPrefix === selectedCategory.prefix) ? "（含下级）" : ""}`}</p></div>}

      {view === "stock" ? (
        stockLoading ? (
          <InventoryLoading />
        ) : stockError ? (
          <LoadError
            title="库存余额暂时无法加载"
            description={`${stockError}。请检查网络连接后重试。`}
            onRetry={() => {
              setStockLoading(true);
              setStockError("");
              setStockReload((value) => value + 1);
            }}
          />
        ) : hideRootStock ? null : stock && stock.stock.length > 0 ? (
          <section className="admin-section flush">
            {isPhone ? <section className="mobile-sku-list" aria-label="库存商品规格">
              {stock.stock.map(row => <MobileSkuCard key={row.skuId ?? `product:${row.productId}`} productId={row.productId} imageUrl={row.imageUrl}
                code={row.productCode} name={row.productName} specification={row.skuId ? row.specification || row.skuCode || "未填写" : "待添加规格"} unit={row.unit ?? "—"} skuCode={row.skuCode ?? undefined} reserveImage>
                <div className="staff-catalog-status"><StatusBadge tone={row.active && row.productActive ? "success" : "neutral"}>{!row.productActive ? "已停用" : !row.skuId ? "待添加规格" : row.active ? "在用" : "已停用"}</StatusBadge></div>
                <div className="mobile-sku__order"><span className="staff-catalog-price"><small>当前库存</small><strong className={row.quantity !== null && row.quantity < 0 ? "stock-negative" : !row.quantity ? "stock-zero" : "stock-value"}>{row.quantity ?? "—"}</strong><small>{row.unit ?? ""}</small></span>
                  {canManageProducts && <Button variant="secondary" aria-label={`编辑商品 ${row.productCode} ${row.productName}`} onClick={() => setProductEditor({ id: row.productId, code: row.productCode, name: row.productName, active: row.productActive, stock: [] })}><PencilSimple />编辑商品</Button>}
                </div>
              </MobileSkuCard>)}
            </section> : <div className="data-table inventory-stock-table">
              <div className="data-table__head">
                <span>商品编码 / 名称</span>
                <span>规格</span>
                <span>单位</span>
                <span>当前库存</span>
                <span>目录状态</span>
              </div>
              {stock.stock.map((row) => (
                <div className="data-table__row" key={row.skuId ?? `product:${row.productId}`}>
                  <span data-label="商品" className="inventory-product-cell">
                    <span className="product-image inventory-product-image"><ProductImage productId={row.productId} imageUrl={row.imageUrl ?? null} alt={`${row.productName}主图`} /></span>
                    <span className="inventory-product-details"><b>{row.productName}</b>
                      <small>{row.productCode}</small>
                      {canManageProducts && <button className="inventory-product-edit" aria-label={`编辑商品 ${row.productCode} ${row.productName}`}
                        onClick={() => setProductEditor({ id: row.productId, code: row.productCode, name: row.productName, active: row.productActive, stock: [] })}><PencilSimple />编辑商品</button>}
                    </span>
                  </span>
                  <span data-label="规格">
                    <b>{row.skuId ? row.specification || row.skuCode : "待添加规格"}</b>
                    <small>{row.skuId ? `规格编码 ${row.skuCode}` : "请在商品目录添加规格"}</small>
                  </span>
                  <span data-label="单位">{row.unit ?? "—"}</span>
                  <span data-label="当前库存">
                    <strong className={row.quantity !== null && row.quantity < 0 ? "stock-negative" : !row.quantity ? "stock-zero" : "stock-value"}>
                      {row.quantity ?? "—"}
                    </strong>
                  </span>
                  <span data-label="目录状态">
                    <StatusBadge
                      tone={row.active && row.productActive ? "success" : "neutral"}
                    >
                      {!row.productActive ? "已停用" : !row.skuId ? "待添加规格" : row.active ? "在用" : "已停用"}
                    </StatusBadge>
                  </span>
                </div>
              ))}
            </div>}
            <InventoryPagination
              pagination={stock.pagination}
              onPage={(page) => {
                setStockLoading(true);
                setStockError("");
                setStockPage(page);
              }}
            />
          </section>
        ) : (
          <EmptyState
            title="没有匹配的库存 SKU"
            description="修改或清空搜索内容，列表会自动更新。"
          />
        )
      ) : movementLoading ? (
        <InventoryLoading />
      ) : movementError ? (
        <LoadError
          title="库存流水暂时无法加载"
          description={`${movementError}。请检查网络连接后重试。`}
          onRetry={() => {
            setMovementLoading(true);
            setMovementError("");
            setMovementReload((value) => value + 1);
          }}
        />
      ) : movements && movements.movements.length > 0 ? (
        <section className="inventory-movement-list">
          {movements.movements.map((movement) => {
            const meta = movementLabels[movement.movementType];
            return (
              <article
                className={
                  movement.reversedAt
                    ? "inventory-movement reversed"
                    : "inventory-movement"
                }
                key={movement.id}
              >
                <details>
                  <summary>
                    <span className="movement-document">
                      <small>{formatDate(movement.occurredOn)}</small>
                      <b>{movement.documentNo}</b>
                    </span>
                    <StatusBadge tone={movement.reversedAt ? "neutral" : meta.tone}>
                      {movement.reversedAt ? "已冲销" : meta.label}
                    </StatusBadge>
                    <span className="movement-counterparty">
                      <small>往来单位</small>
                      <b>{movement.counterparty || "—"}</b>
                    </span>
                    <span className="movement-quantity">
                      <small>SKU / 数量</small>
                      <b>
                        {movement.lines.length} 项 · {totalMovementQuantity(movement)}
                      </b>
                    </span>
                    <span className={`movement-direction ${meta.direction}`}>
                      {meta.direction === "in" ? <ArrowDown /> : <ArrowUp />}
                    </span>
                  </summary>
                  <div className="inventory-movement__detail">
                    <div className="inventory-movement__meta">
                      <span>关联单号：{movement.referenceNo || "—"}</span>
                      {movement.orderId && <span>由订单出货自动生成</span>}
                      <span>登记人：{movement.actorName}</span>
                      <span>备注：{movement.note || "—"}</span>
                    </div>
                    <div className="inventory-movement__lines">
                      {movement.lines.map((line) => (
                        <div key={line.skuId}>
                          <span>
                            <b>{line.productName}</b>
                            <small>
                              {line.productCode} · {line.skuCode} · {line.specification}
                            </small>
                          </span>
                          <strong>
                            {meta.direction === "in" ? "+" : "−"}
                            {line.quantity} {line.unit}
                          </strong>
                        </div>
                      ))}
                    </div>
                    {movement.reversedAt ? (
                      <p className="inventory-reversal-note">
                        冲销原因：{movement.reversalReason}
                      </p>
                    ) : movement.orderId ? (
                      <p className="inventory-reversal-note">此单据关联订单出货，不能单独冲销。</p>
                    ) : (
                      isAdmin && (
                        <footer>
                          <Button
                            variant="ghost"
                            onClick={() => setReverseTarget(movement)}
                          >
                            <ArrowsCounterClockwise />
                            冲销这张单据
                          </Button>
                        </footer>
                      )
                    )}
                  </div>
                </details>
              </article>
            );
          })}
          <InventoryPagination
            pagination={movements.pagination}
            onPage={(page) => {
              setMovementLoading(true);
              setMovementError("");
              setMovementPage(page);
            }}
          />
        </section>
      ) : (
        <EmptyState
          title="还没有库存流水"
          description="登记采购入库、销售出库或盘点调整后，流水会保留在这里。"
          action={
            isAdmin ? (
              <Button onClick={() => setMovementModalType("PURCHASE_IN")}>
                <Plus />登记第一笔采购入库
              </Button>
            ) : undefined
          }
        />
      )}
      </div>
      </DealerCatalogLayout>}
      {canManageCategories && categoryEditor && <CodeCategoryEditor category={categoryEditor} onClose={() => setCategoryEditor(null)} onSaved={() => {
        notify("分类名称和显示顺序已保存"); setCategoryEditor(null); reloadAll();
      }} />}
      {canManageProducts && productEditor && <CategoryProductEditor product={productEditor} onClose={() => setProductEditor(null)} onSaved={() => {
        notify("商品已更新，分类和库存已刷新"); setProductEditor(null); setStockPage(1);
      }} />}

      {isAdmin && movementModalType && (
        <InventoryMovementModal
          initialMovementType={movementModalType}
          onPurchaseImport={onPurchaseImport ? () => { setMovementModalType(null); onPurchaseImport(); } : undefined}
          onClose={() => setMovementModalType(null)}
          onDone={(movement) => {
            setMovementModalType(null);
            notify(`${movement.documentNo} 已登记`);
            reloadAll();
            setView("movements");
            movementSearch.reset(movementSearch.query);
          }}
        />
      )}
      {isAdmin && reverseTarget && (
        <InventoryReverseModal
          movement={reverseTarget}
          onClose={() => setReverseTarget(null)}
          onDone={() => {
            notify(`${reverseTarget.documentNo} 已冲销`);
            setReverseTarget(null);
            reloadAll();
          }}
        />
      )}
    </>
  );
}

function InventoryPagination({
  pagination,
  onPage,
}: {
  pagination: Pagination;
  onPage: (page: number) => void;
}) {
  if (pagination.totalPages <= 1) return null;
  return (
    <nav className="inventory-pagination" aria-label="库存分页">
      <Button
        variant="secondary"
        disabled={pagination.page <= 1}
        onClick={() => onPage(pagination.page - 1)}
      >
        上一页
      </Button>
      <span>
        第 {pagination.page} / {pagination.totalPages} 页 · 共 {pagination.total} 条
      </span>
      <Button
        variant="secondary"
        disabled={pagination.page >= pagination.totalPages}
        onClick={() => onPage(pagination.page + 1)}
      >
        下一页
      </Button>
    </nav>
  );
}

function InventoryLoading() {
  return (
    <div className="loading-block" role="status" aria-label="正在加载库存数据">
      <span className="skeleton" />
      <span className="skeleton" />
      <span className="skeleton" />
    </div>
  );
}

type SelectedInventoryLine = InventorySkuOption & { quantity: string };

function InventoryMovementModal({
  initialMovementType,
  onPurchaseImport,
  onClose,
  onDone,
}: {
  initialMovementType: InventoryMovementType;
  onPurchaseImport?: (() => void) | undefined;
  onClose: () => void;
  onDone: (movement: InventoryMovement) => void;
}) {
  const isPhone = usePhoneLayout();
  const idempotencyKeyRef = useRef(crypto.randomUUID());
  const searchSequence = useRef(0);
  const [form, setForm] = useState({
    movementType: initialMovementType,
    occurredOn: todayInShanghai(),
    counterparty: "",
    referenceNo: "",
    note: "",
  });
  const [searchCategory, setSearchCategory] = useState("");
  const [searchPage, setSearchPage] = useState(1);
  const [catalog, setCatalog] = useState<CatalogResponse | null>(null);
  const codeCategories = useSynchronizedCodeCategories(catalog?.codeCategories, 'catalog');
  const [skuResults, setSkuResults] = useState<InventorySkuOption[]>([]);
  const [searchLoading, setSearchLoading] = useState(true);
  const [searchError, setSearchError] = useState("");
  const skuSearch = useProductSearch({ onCommit: () => {
    setSearchPage(1); setSearchLoading(true); setSearchError("");
  } });
  const [lines, setLines] = useState<SelectedInventoryLine[]>([]);
  const [importBusy, setImportBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const sequence = ++searchSequence.current;
    let current = true;
    const controller = new AbortController();
    const path = productSearchPath("/api/v1/admin/inventory/catalog", skuSearch.query, searchCategory, { page: searchPage, pageSize: 20 });
    void request<CatalogResponse>(path, { signal: controller.signal })
        .then((result) => {
          if (!current || sequence !== searchSequence.current) return;
          setCatalog(result);
          setSkuResults(result.products.flatMap((product) => product.skus.map((sku) => ({
            id: sku.id, code: sku.code, specification: sku.specification, unit: sku.unit,
            active: true, productActive: true, productId: sku.productId ?? product.id,
            imageUrl: sku.imageUrl === undefined ? product.imageUrl ?? null : sku.imageUrl, productCode: product.code,
            productName: sku.productName?.trim() || product.name, stockQuantity: sku.stockQuantity ?? 0,
          }))));
        })
        .catch((caught: unknown) => {
          if (current && sequence === searchSequence.current) {
            setSearchError(loadErrorMessage(caught, "SKU 搜索失败，请重试"));
          }
        })
        .finally(() => {
          if (current && sequence === searchSequence.current) setSearchLoading(false);
        });
    return () => { current = false; controller.abort(); };
  }, [skuSearch.query, skuSearch.revision, searchCategory, searchPage]);

  function addSku(sku: InventorySkuOption) {
    if (lines.some((line) => line.id === sku.id)) return;
    if (lines.length >= 100) { setError("一张库存单最多添加 100 个 SKU"); return; }
    setLines((current) => [...current, { ...sku, quantity: "1" }]);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    if (lines.length === 0) {
      setError("请先搜索并添加至少一个 SKU");
      return;
    }
    const parsedLines = lines.map((line) => ({
      skuId: line.id,
      quantity: Number(line.quantity),
    }));
    if (
      parsedLines.some(
        (line) => !Number.isInteger(line.quantity) || line.quantity <= 0,
      )
    ) {
      setError("数量必须是大于零的整数");
      return;
    }
    setLoading(true);
    try {
      const result = await request<{ movement: InventoryMovement }>(
        "/api/v1/admin/inventory/movements",
        {
          method: "POST",
          body: JSON.stringify({
            idempotencyKey: idempotencyKeyRef.current,
            ...form,
            lines: parsedLines,
          }),
        },
      );
      onDone(result.movement);
    } catch (caught) {
      setError(loadErrorMessage(caught, "库存单据登记失败"));
    } finally {
      setLoading(false);
    }
  }

  const movementMeta = movementLabels[form.movementType];
  const counterpartyLabel =
    form.movementType === "PURCHASE_IN"
      ? "供应商"
      : form.movementType === "SALES_OUT"
        ? "客户 / 领用方"
        : "调整说明";
  const visibleSearchCategory = effectiveProductCategory(skuSearch.query, searchCategory);
  const selectedSearchCategory = codeCategories?.find((node) => `code:${node.prefix}` === visibleSearchCategory);
  const searchCategoryHasChildren = selectedSearchCategory && codeCategories?.some((node) => node.parentPrefix === selectedSearchCategory.prefix);
  function selectPhoneCategory(value: string) {
    if (loading) return;
    skuSearch.clear();
    setSearchCategory(value === "all" ? "" : value);
    setSearchPage(1);
    setSearchLoading(true);
    setSearchError("");
  }
  return (
    <Modal
      title={movementLabels[form.movementType].label}
      description="选择商品目录中的成品 SKU 并填写实际数量，提交后更新库存；登记错误可通过冲销更正。"
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            取消
          </Button>
          <Button
            type="submit"
            form="inventory-movement-form"
            loading={loading}
            disabled={importBusy}
          >
            确认登记
          </Button>
        </>
      }
    >
      <form
        id="inventory-movement-form"
        className="inventory-movement-form"
        onSubmit={submit}
      >
        {error && <div className="inline-error" role="alert">{error}</div>}
        <div className="form-grid">
          <Field label="单据类型">
            <select
              value={form.movementType}
              onChange={(event) =>
                setForm({
                  ...form,
                  movementType: event.target.value as InventoryMovementType,
                })
              }
            >
              {Object.entries(movementLabels).map(([value, meta]) => (
                <option key={value} value={value}>
                  {meta.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="发生日期">
            <input
              type="date"
              value={form.occurredOn}
              onChange={(event) =>
                setForm({ ...form, occurredOn: event.target.value })
              }
              required
            />
          </Field>
          <Field label={counterpartyLabel}>
            <input
              value={form.counterparty}
              onChange={(event) =>
                setForm({ ...form, counterparty: event.target.value })
              }
              maxLength={160}
              placeholder="选填"
            />
          </Field>
          <Field label="关联单号">
            <input
              value={form.referenceNo}
              onChange={(event) =>
                setForm({ ...form, referenceNo: event.target.value })
              }
              maxLength={100}
              placeholder="采购单、销售单或盘点单号（选填）"
            />
          </Field>
        </div>

        {form.movementType === "PURCHASE_IN" ? <section className="inventory-purchase-import">
          <div><h3>整批进货</h3><p>表格进货请前往批量导入，自动识别并核对后统一入库。</p></div>
          <Button type="button" variant="secondary" disabled={loading || !onPurchaseImport} onClick={onPurchaseImport}><FileArrowUp />导入表格</Button>
        </section> : <InventorySheetImport
          existingCount={lines.length}
          disabled={loading}
          onBusyChange={setImportBusy}
          onApply={(imported) => {
            setLines(imported.map((line) => ({ ...line, quantity: String(line.quantity) })));
            setError("");
          }}
        />}
        <section className="inventory-sku-picker">
          <header>
            <div>
              <h3>手动选择商品</h3>
              <p>
                {movementMeta.direction === "out"
                  ? "库存不足也可出库，出库后负库存将标红显示"
                  : "按分类或关键词筛选，逐项添加 SKU 并填写数量"}
              </p>
            </div>
            <ProductSearch search={skuSearch} ariaLabel="搜索要登记的库存 SKU" disabled={loading} />
          </header>
          <div className="inventory-sku-filter category-navigation">
            {isPhone ? <MobileDisclosure title="商品分类" description={selectedSearchCategory?.name ?? "全部商品"}
              resetKey={searchCategoryHasChildren ? "" : visibleSearchCategory}>
              <div inert={loading || undefined}>
                <MobileCategoryPicker nodes={codeCategories ?? []} legacyCategories={catalog?.categories ?? []} showLegacyCategories
                  selectedValue={visibleSearchCategory || "all"} onSelect={selectPhoneCategory} />
              </div>
            </MobileDisclosure> : <ProductCategoryFilter nodes={codeCategories ?? []} legacyCategories={catalog?.categories ?? []}
              value={effectiveProductCategory(skuSearch.query, searchCategory)} disabled={loading}
              onChange={(value) => { if (value === searchCategory) return; setSearchCategory(value); setSearchPage(1); setSearchLoading(true); setSearchError(""); }} />}
          </div>
          <div className="inventory-sku-results">
            {searchLoading || skuSearch.pending ? (
              <span className="inventory-search-status">正在搜索…</span>
            ) : searchError ? (
              <span className="inventory-search-status" role="alert">
                {searchError}
              </span>
            ) : skuResults.length === 0 ? (
              <span className="inventory-search-status">没有匹配 SKU</span>
            ) : (
              skuResults.map((sku) => {
                const selected = lines.some((line) => line.id === sku.id);
                return (
                  <button
                    type="button"
                    key={sku.id}
                    disabled={selected || loading || searchLoading || skuSearch.pending || Boolean(searchError)}
                    onClick={() => addSku(sku)}
                  >
                    <span className="inventory-product-cell">
                      <span className="product-image inventory-product-image"><ProductImage productId={sku.productId} imageUrl={sku.imageUrl ?? null} alt={`${sku.productName}主图`} /></span>
                      <span className="inventory-product-details"><b>{sku.productName}</b><small>
                        SKU {sku.code} · {sku.specification} · {sku.unit}
                      </small></span>
                    </span>
                    <span className={sku.stockQuantity < 0 ? "stock-negative" : undefined}>
                      库存 {sku.stockQuantity}
                      <Plus />
                    </span>
                  </button>
                );
              })
            )}
          </div>
          {catalog && catalog.pagination.totalPages > 1 && <nav className="inventory-sku-pagination" aria-label="商品筛选分页">
            <Button type="button" variant="secondary" disabled={searchLoading || searchPage <= 1} onClick={() => { setSearchPage((page) => page - 1); setSearchLoading(true); setSearchError(""); }}>上一页</Button>
            <span>第 {searchPage} / {catalog.pagination.totalPages} 页 · {catalog.pagination.totalSkus ?? catalog.pagination.totalProducts} 个商品</span>
            <Button type="button" variant="secondary" disabled={searchLoading || !catalog.pagination.hasMore} onClick={() => { setSearchPage((page) => page + 1); setSearchLoading(true); setSearchError(""); }}>下一页</Button>
          </nav>}
        </section>

        <section className="inventory-selected-lines">
          <header>
            <h3>本单明细</h3>
            <span>{lines.length} 项</span>
          </header>
          {lines.length === 0 ? (
            <p className="inventory-lines-empty">从上方搜索结果中添加 SKU。</p>
          ) : (
            lines.map((line) => (
              <div key={line.id}>
                <span className="inventory-product-cell">
                  <span className="product-image inventory-product-image"><ProductImage productId={line.productId} imageUrl={line.imageUrl ?? null} alt={`${line.productName}主图`} /></span>
                  <span className="inventory-product-details"><b>{line.productName}</b><small>
                    SKU {line.code} · {line.specification}
                  </small></span>
                </span>
                <label>
                  <span>数量（{line.unit}）</span>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={line.quantity}
                    aria-label={`${line.code} 数量`}
                    onChange={(event) =>
                      setLines((current) =>
                        current.map((item) =>
                          item.id === line.id
                            ? { ...item, quantity: event.target.value }
                            : item,
                        ),
                      )
                    }
                    required
                  />
                </label>
                <Button
                  type="button"
                  variant="ghost"
                  aria-label={`移除 ${line.code}`}
                  onClick={() =>
                    setLines((current) =>
                      current.filter((item) => item.id !== line.id),
                    )
                  }
                >
                  <Trash />
                </Button>
              </div>
            ))
          )}
        </section>
        <Field label="备注">
          <textarea
            value={form.note}
            onChange={(event) => setForm({ ...form, note: event.target.value })}
            maxLength={500}
            placeholder="选填"
          />
        </Field>
      </form>
    </Modal>
  );
}

function InventoryReverseModal({
  movement,
  onClose,
  onDone,
}: {
  movement: InventoryMovement;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  async function submit(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      await request(
        `/api/v1/admin/inventory/movements/${movement.id}/reverse`,
        { method: "POST", body: JSON.stringify({ reason }) },
      );
      onDone();
    } catch (caught) {
      setError(loadErrorMessage(caught, "库存单据冲销失败"));
    } finally {
      setLoading(false);
    }
  }
  return (
    <Modal
      title="冲销库存单据"
      description={`${movement.documentNo} · 冲销会按原单反向恢复库存，并永久保留原始记录。`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            返回
          </Button>
          <Button
            variant="danger"
            type="submit"
            form="inventory-reverse-form"
            loading={loading}
          >
            确认冲销
          </Button>
        </>
      }
    >
      <form id="inventory-reverse-form" onSubmit={submit}>
        {error && <div className="inline-error" role="alert">{error}</div>}
        <Field label="冲销原因">
          <textarea
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            minLength={1}
            maxLength={300}
            required
          />
        </Field>
      </form>
    </Modal>
  );
}
